import { FC, Dispatch } from "react";
import { PdfHandler } from "../../../middleware/handlers/pdf-handler";
import type {
  PdfMarkupStyle,
  PdfMarkupTool,
} from "../../../core/pdf/pdf-scene";

interface PdfMarkupMenuProps {
  markupPanelReveal: boolean;
  markupTool: PdfMarkupTool;
  setMarkupTool: Dispatch<PdfMarkupTool>;
  markupStyle: PdfMarkupStyle;
  setMarkupStyle: Dispatch<PdfMarkupStyle>;
  highlightColor: string;
  setHighlightColor: Dispatch<string>;
  markupModified: boolean;
  // left out for members who may not upload, who can draw but not save
  onSave?: () => void;
  saving: boolean;
}

// how each markup is drawn; pdf.js's editors listen for the gestures named
const markupTools: { tool: PdfMarkupTool; label: string; hint: string }[] = [
  {
    tool: "PEN",
    label: "Pen",
    hint: "Draw freehand on the sheet.",
  },
  {
    tool: "HIGHLIGHT",
    label: "Highlight",
    hint: "Drag over text, or draw where there is none.",
  },
  {
    tool: "TEXT",
    label: "Text note",
    hint: "Click where the note goes, then type.",
  },
  {
    tool: "STAMP",
    label: "Image stamp",
    hint: "Press Add image and pick a PNG or JPG, then drag it into place.",
  },
];

// red first, the colour drawings are marked up in
export const markupColors = [
  { color: "#e03131", label: "Red" },
  { color: "#f08c00", label: "Orange" },
  { color: "#2f9e44", label: "Green" },
  { color: "#1971c2", label: "Blue" },
  { color: "#212529", label: "Black" },
];

export const highlightColors = [
  { color: "#FFFF98", label: "Yellow" },
  { color: "#53FFBC", label: "Green" },
  { color: "#80EBFF", label: "Blue" },
  { color: "#FFCBE6", label: "Pink" },
  { color: "#FF4F5F", label: "Red" },
];

export const PdfMarkupMenu: FC<PdfMarkupMenuProps> = ({
  markupPanelReveal,
  markupTool,
  setMarkupTool,
  markupStyle,
  setMarkupStyle,
  highlightColor,
  setHighlightColor,
  markupModified,
  onSave,
  saving,
}) => {
  if (!markupPanelReveal) return null;

  const isHighlight = markupTool === "HIGHLIGHT";
  const swatches = isHighlight ? highlightColors : markupColors;
  const selectedColor = isHighlight ? highlightColor : markupStyle.color;

  return (
    <div className="viewerToolPanel">
      <div className="viewerToolPanelHeader">
        <span className="viewerToolPanelLabel">MARKUP</span>
        <span className="viewerToolPanelName">Pick a markup tool</span>
        {markupTool === "STAMP" ? (
          <div className="pdfMarkupStyle">
            <input
              type="button"
              className="pdfMarkupAddImage"
              value="Add image"
              onClick={() => {
                PdfHandler.AddStamp();
              }}
            />
          </div>
        ) : (
          <div className="pdfMarkupStyle">
            <div className="pdfMarkupSwatches">
              {swatches.map(({ color, label }) => (
                <input
                  key={color}
                  type="button"
                  title={label}
                  aria-label={label}
                  className={
                    color === selectedColor
                      ? "pdfMarkupSwatch pdfMarkupSwatchSelected"
                      : "pdfMarkupSwatch"
                  }
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    if (isHighlight) {
                      setHighlightColor(color);
                    } else {
                      setMarkupStyle({ ...markupStyle, color });
                    }
                  }}
                />
              ))}
            </div>
            {markupTool === "PEN" ? (
              <label className="pdfMarkupRange">
                <span>Thickness {markupStyle.thickness}</span>
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={markupStyle.thickness}
                  onChange={(event) => {
                    setMarkupStyle({
                      ...markupStyle,
                      thickness: Number(event.target.value),
                    });
                  }}
                />
              </label>
            ) : markupTool === "TEXT" ? (
              <label className="pdfMarkupRange">
                <span>Text size {markupStyle.fontSize}</span>
                <input
                  type="range"
                  min={8}
                  max={72}
                  value={markupStyle.fontSize}
                  onChange={(event) => {
                    setMarkupStyle({
                      ...markupStyle,
                      fontSize: Number(event.target.value),
                    });
                  }}
                />
              </label>
            ) : (
              <></>
            )}
          </div>
        )}
      </div>
      <div className="viewerToolRowWrapper">
        {markupTools.map(({ tool, label, hint }) => (
          <div
            key={tool}
            className={
              tool === markupTool
                ? "viewerToolRow viewerToolRowSelected"
                : "viewerToolRow"
            }
            onClick={() => {
              setMarkupTool(tool);
            }}
          >
            <span className="viewerToolRowLabel">{label}</span>
            <span className="viewerToolRowHint">{hint}</span>
          </div>
        ))}
      </div>
      <div className="viewerToolPanelFooter">
        <span>
          Click a markup to move, resize or delete it. Markups are written into
          the PDF, so Bluebeam, Acrobat and other viewers show them too.
        </span>
        {onSave ? (
          <input
            type="button"
            value={saving ? "Saving..." : "Save as new version"}
            disabled={!markupModified || saving}
            onClick={onSave}
          />
        ) : (
          <span>
            Your role in this project can't upload, so markups can't be saved
            and go when the viewer closes.
          </span>
        )}
      </div>
    </div>
  );
};

export default PdfMarkupMenu;
